import React, { useEffect, useState, useMemo } from 'react';
import { financeiroService } from '../services/financeiroService';
import { supabase } from '../lib/supabase';
import { FirebaseAuthService } from '../services/firebaseAuthService';
import { Conta, Cobranca, GatewayPagamento, Banco, TipoConta, FormaPagamento, TipoContaId } from '../types/financeiro';
import { DollarSign, AlertCircle, ShieldAlert, Lock } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { ContaFinanceiraModal } from '../components/financeiro/ContaFinanceiraModal';
import { BaixaPagamentoModal } from '../components/financeiro/BaixaPagamentoModal';
import { VisualizarComprovanteModal } from '../components/financeiro/VisualizarComprovanteModal';
import { ConfirmDeleteModal } from '../components/financeiro/ConfirmDeleteModal';
import { EmitirNfseModal } from '../components/fiscal/EmitirNfseModal';
import { FluxoCaixaDashboard } from '../components/financeiro/FluxoCaixaDashboard';
import { FinanceiroResumoCards } from '../components/financeiro/FinanceiroResumoCards';
import { FinanceiroToolbar } from '../components/financeiro/FinanceiroToolbar';
import { FinanceiroTabsNavigation, FinanceiroTab } from '../components/financeiro/FinanceiroTabsNavigation';
import { FinanceiroEntradasSection } from '../components/financeiro/FinanceiroEntradasSection';
import { FinanceiroSaidasSection } from '../components/financeiro/FinanceiroSaidasSection';
import { FinanceiroCobrancasSection } from '../components/financeiro/FinanceiroCobrancasSection';
import { FinanceiroGatewaysSection } from '../components/financeiro/FinanceiroGatewaysSection';
import { FinanceiroBancosSection } from '../components/financeiro/FinanceiroBancosSection';
import { ExtratoCnpjBancoSection } from '../components/financeiro/ExtratoCnpjBancoSection';
import { pessoaService } from '../services/pessoaService';
import { empresaService } from '../services/empresaService';
import { Pessoa } from '../types/pessoa';
import { EmpresaConfig } from '../types/empresa';
import { pessoaBelongsToGroup } from '../utils/pessoaGroupUtils';
import { FiltroEClassificacaoBar, FilterSortState } from '../components/common/FiltroEClassificacaoBar';

export function FinanceiroPage() {
  const { hasPermission, canEdit, canDelete } = usePermissions();
  const [tab, setTab] = useState<FinanceiroTab>('entradas');
  const [contas, setContas] = useState<Conta[]>([]);
  const [cobrancas, setCobrancas] = useState<Cobranca[]>([]);
  const [gateways, setGateways] = useState<GatewayPagamento[]>([]);
  const [bancos, setBancos] = useState<Banco[]>([]);
  const [tiposConta, setTiposConta] = useState<TipoConta[]>([]);
  const [formas, setFormas] = useState<FormaPagamento[]>([]);
  const [pessoas, setPessoas] = useState<Pessoa[]>([]);
  const [empresas, setEmpresas] = useState<EmpresaConfig[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters & Sorting State
  const [filters, setFilters] = useState<FilterSortState>({
    searchTerm: '',
    statusFilter: 'all',
    sortBy: 'data',
    sortOrder: 'asc',
    startDate: '',
    endDate: '',
    categoryFilter: 'all',
    groupFilter: 'all',
    bancoFilter: 'all',
    cnpjFilter: 'all',
  });
  const [loadErrors, setLoadErrors] = useState<string[]>([]);

  // Modals State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConta, setSelectedConta] = useState<Conta | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'quitar' | 'view'>('create');
  const [modalType, setModalType] = useState<TipoContaId>(TipoContaId.RECEBER);

  // Baixa Modal State
  const [isBaixaModalOpen, setIsBaixaModalOpen] = useState(false);
  const [contaParaBaixa, setContaParaBaixa] = useState<Conta | null>(null);

  // Visualizar Comprovante Modal State
  const [isComprovanteModalOpen, setIsComprovanteModalOpen] = useState(false);
  const [contaComprovante, setContaComprovante] = useState<Conta | null>(null);

  // Delete Confirmation Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contaToDelete, setContaToDelete] = useState<Conta | null>(null);

  // NFS-e Emissão Modal State (Contas a Receber)
  const [isNfseModalOpen, setIsNfseModalOpen] = useState(false);
  const [contaParaNfse, setContaParaNfse] = useState<Conta | null>(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Map for fast person name lookup
  const pessoasMap = useMemo(() => {
    const map = new Map<number, string>();
    pessoas.forEach(p => map.set(p.id, p.nome_fantasia || p.nome));
    return map;
  }, [pessoas]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [tab, filters]);

  async function fetchData() {
    setLoading(true);
    setLoadErrors([]);
    const errors: string[] = [];

    const fetchSafe = async <T,>(promise: Promise<T>, fallback: T, name: string): Promise<T> => {
      try {
        return await promise;
      } catch (err: any) {
        console.error(`Erro ao carregar ${name}:`, err);
        
        if (err?.code === '42501' || err?.message?.includes('permission denied')) {
          errors.push(`${name}: Acesso restrito por permissão de perfil.`);
          return fallback;
        }

        errors.push(`${name}: ${err.message || err}`);
        return fallback;
      }
    };

    try {
      const [listContas, listCobrancas, listGateways, listBancos, listTipos, listFormas, listPessoas, listEmpresas] = await Promise.all([
        fetchSafe(financeiroService.getContas(), [], 'Lançamentos (tabela conta)'),
        fetchSafe(financeiroService.getCobrancas(), [], 'Cobranças (tabela cobranca)'),
        fetchSafe(financeiroService.getGateways(), [], 'Gateways (tabela gateway_pagamento)'),
        fetchSafe(financeiroService.getBancos(), [], 'Bancos (tabela banco)'),
        fetchSafe(financeiroService.getTiposConta(), [], 'Tipos de Conta (tabela tipo_conta)'),
        fetchSafe(financeiroService.getFormasPagamento(), [], 'Formas de Pagamento (tabela forma_pagamento)'),
        fetchSafe(pessoaService.getAll(), [], 'Pessoas/Clientes (tabela pessoa)'),
        fetchSafe(empresaService.getEmpresas(), [], 'Empresas Cadastradas')
      ]);

      setContas(listContas);
      setCobrancas(listCobrancas);
      setGateways(listGateways);
      setBancos(listBancos);
      setTiposConta(listTipos);
      setFormas(listFormas);
      setPessoas(listPessoas);
      setEmpresas(listEmpresas);
      setLoadErrors(errors);
    } catch (err: any) {
      console.error('Error loading financeiro:', err);
      setLoadErrors([err.message || 'Erro inesperado']);
    } finally {
      setLoading(false);
    }
  }

  const handleCreate = (type: TipoContaId) => {
    if (!hasPermission('create_financial') && !hasPermission('manage_financial')) {
      alert('Você não tem permissão para cadastrar lançamentos financeiros.');
      return;
    }
    setSelectedConta(null);
    setModalMode('create');
    setModalType(type);
    setIsModalOpen(true);
  };

  const handleView = (conta: Conta) => {
    setSelectedConta(conta);
    setModalMode('view');
    setModalType(conta.tipo_conta_id as TipoContaId);
    setIsModalOpen(true);
  };

  const handleEdit = (conta: Conta) => {
    if (!hasPermission('manage_financial')) {
      alert('Você não tem permissão para editar lançamentos financeiros.');
      return;
    }
    setSelectedConta(conta);
    setModalMode('edit');
    setModalType(conta.tipo_conta_id as TipoContaId);
    setIsModalOpen(true);
  };

  const handleOpenBaixa = (conta: Conta) => {
    if (!hasPermission('pay_financial')) {
      alert('Você não tem permissão para dar baixa em lançamentos financeiros.');
      return;
    }
    setContaParaBaixa(conta);
    setIsBaixaModalOpen(true);
  };

  const handleOpenComprovante = (conta: Conta) => {
    setContaComprovante(conta);
    setIsComprovanteModalOpen(true);
  };

  const handleOpenEmitirNfse = (conta: Conta) => {
    setContaParaNfse(conta);
    setIsNfseModalOpen(true);
  };

  const openDeleteModal = (conta: Conta) => {
    if (!hasPermission('delete_financial')) {
      alert('Você não tem permissão para excluir lançamentos financeiros.');
      return;
    }
    setContaToDelete(conta);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!contaToDelete) return;
    if (!hasPermission('delete_financial')) {
      alert('Você não tem permissão para excluir lançamentos financeiros.');
      setContaToDelete(null);
      return;
    }
    try {
      await financeiroService.deleteConta(contaToDelete.id);
      await fetchData();
    } catch (err: any) {
      alert('Erro ao excluir: ' + err.message);
    } finally {
      setContaToDelete(null);
    }
  };

  const getStatus = (conta: Conta): 'atrasada' | 'aberta' | 'quitada' => {
    if (conta.dt_pagamento) return 'quitada';
    if (!conta.dt_vencimento) return 'aberta';
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const vencimento = new Date(conta.dt_vencimento + 'T00:00:00');

    return vencimento < today ? 'atrasada' : 'aberta';
  };

  const filterAndSortContas = (typeId: TipoContaId) => {
    return contas
      .filter((c) => {
        const matchesType = Number(c.tipo_conta_id) === typeId;
        if (!matchesType) return false;

        const term = filters.searchTerm.toLowerCase();
        const personObj = pessoas.find((p) => Number(p.id) === Number(c.pessoa_id));
        const personName = (personObj?.nome || '').toLowerCase();
        const personFantasy = (personObj?.nome_fantasia || '').toLowerCase();
        const personDoc = (personObj?.documento || '').replace(/\D/g, '');
        const nfTerm = (c.numero_nf || '').toLowerCase();

        const matchesSearch =
          !term ||
          c.descricao?.toLowerCase().includes(term) ||
          c.id.toString().includes(term) ||
          personName.includes(term) ||
          personFantasy.includes(term) ||
          nfTerm.includes(term) ||
          (c.banco_nome || '').toLowerCase().includes(term) ||
          (c.cnpj_faturador || '').toLowerCase().includes(term) ||
          (term.replace(/\D/g, '').length > 0 && personDoc.includes(term.replace(/\D/g, '')));

        const status = getStatus(c);
        const matchesStatus =
          filters.statusFilter === 'all' ||
          (filters.statusFilter === 'atrasadas' && status === 'atrasada') ||
          (filters.statusFilter === 'abertas' && status === 'aberta') ||
          (filters.statusFilter === 'quitadas' && status === 'quitada');

        const matchesStartDate = !filters.startDate || (c.dt_vencimento && c.dt_vencimento >= filters.startDate);
        const matchesEndDate = !filters.endDate || (c.dt_vencimento && c.dt_vencimento <= filters.endDate);

        // Group Filter
        const matchesGroup =
          !filters.groupFilter ||
          filters.groupFilter === 'all' ||
          pessoaBelongsToGroup(personObj, filters.groupFilter);

        // Person / Category Filter
        const matchesCategory =
          !filters.categoryFilter ||
          filters.categoryFilter === 'all' ||
          c.pessoa_id?.toString() === filters.categoryFilter;

        // CNPJ Filter
        let matchesCnpj = true;
        if (filters.cnpjFilter && filters.cnpjFilter !== 'all') {
          const cFaturador = (c.cnpj_faturador || '').toLowerCase();
          const filterLower = filters.cnpjFilter.toLowerCase();
          
          if (filterLower.includes('23.059') || filterLower.includes('matriz') || filterLower === 'voope (cnpj 1)') {
            matchesCnpj = cFaturador.includes('23.059') || cFaturador.includes('matriz') || (cFaturador.includes('voope') && !cFaturador.includes('two'));
          } else if (filterLower.includes('two') || filterLower === 'voope two (cnpj 2)') {
            matchesCnpj = cFaturador.includes('two');
          } else {
            // Match against new dynamic label
            const cnpjMatch = filters.cnpjFilter.split(' - ')[0]?.trim().toLowerCase();
            if (cnpjMatch) {
              matchesCnpj = cFaturador.includes(cnpjMatch);
            } else {
              matchesCnpj = cFaturador.includes(filterLower);
            }
          }
        }

        // Banco Filter
        let matchesBanco = true;
        if (filters.bancoFilter && filters.bancoFilter !== 'all') {
          const bNome = (c.banco_nome || '').toLowerCase();
          matchesBanco = bNome.includes(filters.bancoFilter.toLowerCase());
        }

        return (
          matchesSearch &&
          matchesStatus &&
          matchesStartDate &&
          matchesEndDate &&
          matchesGroup &&
          matchesCategory &&
          matchesCnpj &&
          matchesBanco
        );
      })
      .sort((a, b) => {
        let comparison = 0;
        if (filters.sortBy === 'data' || filters.sortBy === 'vencimento') {
          comparison = (a.dt_vencimento || '').localeCompare(b.dt_vencimento || '');
        } else if (filters.sortBy === 'valor') {
          comparison = (a.valor || 0) - (b.valor || 0);
        } else if (filters.sortBy === 'id') {
          comparison = a.id - b.id;
        } else if (filters.sortBy === 'descricao') {
          comparison = (a.descricao || '').localeCompare(b.descricao || '');
        }
        return filters.sortOrder === 'asc' ? comparison : -comparison;
      });
  };

  const filteredEntradas = useMemo(() => filterAndSortContas(TipoContaId.RECEBER), [contas, filters, pessoas]);
  const paginatedEntradas = useMemo(() => {
    return filteredEntradas.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [filteredEntradas, currentPage, itemsPerPage]);

  const filteredSaidas = useMemo(() => filterAndSortContas(TipoContaId.PAGAR), [contas, filters, pessoas]);
  const paginatedSaidas = useMemo(() => {
    return filteredSaidas.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [filteredSaidas, currentPage, itemsPerPage]);

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      statusFilter: 'all',
      sortBy: 'data',
      sortOrder: 'asc',
      startDate: '',
      endDate: '',
      groupFilter: 'all',
      categoryFilter: 'all',
      bancoFilter: 'all',
      cnpjFilter: 'all',
    });
  };

  const entradasTotalCount = useMemo(
    () => contas.filter((c) => Number(c.tipo_conta_id) === TipoContaId.RECEBER).length,
    [contas]
  );
  const saidasTotalCount = useMemo(
    () => contas.filter((c) => Number(c.tipo_conta_id) === TipoContaId.PAGAR).length,
    [contas]
  );

  if (!hasPermission('view_financial')) {
    return (
      <div className="min-h-[450px] flex flex-col items-center justify-center p-8 bg-white rounded-3xl border border-slate-200 text-center shadow-xs">
        <div className="w-16 h-16 bg-rose-50 text-rose-600 rounded-2xl flex items-center justify-center mb-4">
          <ShieldAlert className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-black text-slate-900">Acesso Restrito ao Módulo Financeiro</h2>
        <p className="text-sm text-slate-500 mt-2 max-w-md">
          Seu perfil de usuário não possui a permissão <span className="font-mono text-rose-600 font-bold bg-rose-50 px-1.5 py-0.5 rounded">view_financial</span>.
          Entre em contato com o administrador do sistema para solicitar acesso.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Principal */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2.5">
            <div className="p-2 bg-red-100 text-red-600 rounded-xl">
              <DollarSign className="h-6 w-6" />
            </div>
            Gestão Financeira
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Contas a Pagar (Saídas), Contas a Receber (Entradas), Baixas com Comprovante e Fluxo de Caixa.
          </p>
        </div>
      </div>

      {loadErrors.length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-bold text-red-800">Aviso de Conexão com o Banco de Dados</h4>
            <p className="text-xs text-red-700 mt-1">
              Algumas consultas retornaram avisos:
            </p>
            <ul className="list-disc list-inside text-xs text-red-600 mt-1 space-y-0.5">
              {loadErrors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Cards de Resumo Financeiro */}
      <FinanceiroResumoCards
        contas={[...filteredEntradas, ...filteredSaidas]}
        bancosCount={bancos.length}
        activeTab={tab}
      />

      {/* Toolbar Unificada e Ações Rápidas */}
      <FinanceiroToolbar
        statusFilter={filters.statusFilter}
        onStatusChange={(newStatus) => setFilters((prev) => ({ ...prev, statusFilter: newStatus }))}
        onRefresh={fetchData}
        onClearFilters={clearFilters}
        onCreatePagar={() => handleCreate(TipoContaId.PAGAR)}
        onCreateReceber={() => handleCreate(TipoContaId.RECEBER)}
        loading={loading}
      />

      {/* Navegação de Abas e Seções */}
      <FinanceiroTabsNavigation
        activeTab={tab}
        onTabChange={setTab}
        entradasCount={entradasTotalCount}
        saidasCount={saidasTotalCount}
        cobrancasCount={cobrancas.length}
        gatewaysCount={gateways.length}
        bancosCount={bancos.length}
      />

      {/* Filtro e Classificação Avançada (Apenas para abas de Contas a Receber e a Pagar) */}
      {(tab === 'entradas' || tab === 'saidas') && (
        <FiltroEClassificacaoBar
          filters={filters}
          onChange={setFilters}
          pessoas={pessoas}
          bancos={bancos}
          empresas={empresas}
          showBankAndCnpj={true}
          statusOptions={[
            { value: 'all', label: 'Todos os Status' },
            { value: 'abertas', label: 'Em Aberto / Pendentes' },
            { value: 'quitadas', label: 'Quitadas / Pagas' },
            { value: 'atrasadas', label: 'Atrasadas / Vencidas' },
          ]}
          sortOptions={[
            { value: 'data', label: 'Data de Vencimento' },
            { value: 'valor', label: 'Valor (R$)' },
            { value: 'id', label: 'Código (ID)' },
            { value: 'descricao', label: 'Descrição' },
          ]}
          showCategories={true}
          placeholderSearch="Pesquisar por descrição, cliente, fornecedor, NF, ID, banco..."
          totalResults={tab === 'entradas' ? filteredEntradas.length : filteredSaidas.length}
        />
      )}

      {/* Renderização das Seções Separadas */}
      {tab === 'entradas' && (
        <FinanceiroEntradasSection
          contas={filteredEntradas}
          paginatedContas={paginatedEntradas}
          pessoasMap={pessoasMap}
          loading={loading}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
          onCreate={() => handleCreate(TipoContaId.RECEBER)}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={openDeleteModal}
          onBaixa={handleOpenBaixa}
          onViewComprovante={handleOpenComprovante}
          onEmitirNfse={handleOpenEmitirNfse}
        />
      )}

      {tab === 'saidas' && (
        <FinanceiroSaidasSection
          contas={filteredSaidas}
          paginatedContas={paginatedSaidas}
          pessoasMap={pessoasMap}
          loading={loading}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
          onCreate={() => handleCreate(TipoContaId.PAGAR)}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={openDeleteModal}
          onBaixa={handleOpenBaixa}
          onViewComprovante={handleOpenComprovante}
        />
      )}

      {tab === 'extrato' && (
        <ExtratoCnpjBancoSection
          contas={contas}
          bancos={bancos}
          empresas={empresas}
          pessoasMap={pessoasMap}
          loading={loading}
        />
      )}

      {tab === 'fluxo' && <FluxoCaixaDashboard />}

      {tab === 'cobrancas' && (
        <FinanceiroCobrancasSection cobrancas={cobrancas} loading={loading} />
      )}

      {tab === 'gateways' && (
        <FinanceiroGatewaysSection gateways={gateways} loading={loading} />
      )}

      {tab === 'bancos' && (
        <FinanceiroBancosSection bancos={bancos} loading={loading} onRefresh={fetchData} />
      )}

      {/* Modal Principal de Cadastro e Edição */}
      <ContaFinanceiraModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={fetchData}
        selectedConta={selectedConta}
        mode={modalMode}
        tipoConta={modalType}
      />

      {/* Modal Dedicado para Dar Baixa / Quitar */}
      <BaixaPagamentoModal
        isOpen={isBaixaModalOpen}
        onClose={() => {
          setIsBaixaModalOpen(false);
          setContaParaBaixa(null);
        }}
        onSuccess={fetchData}
        conta={contaParaBaixa}
        pessoasMap={pessoasMap}
      />

      {/* Modal de Visualização de Comprovante */}
      <VisualizarComprovanteModal
        isOpen={isComprovanteModalOpen}
        onClose={() => {
          setIsComprovanteModalOpen(false);
          setContaComprovante(null);
        }}
        conta={contaComprovante}
        pessoaNome={contaComprovante ? (pessoasMap.get(contaComprovante.pessoa_id) || '') : ''}
      />

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setContaToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão"
        itemDescription={
          contaToDelete
            ? `#${contaToDelete.id} - ${contaToDelete.descricao || 'Lançamento sem descrição'} (R$ ${contaToDelete.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })})`
            : undefined
        }
      />

      {/* Modal de Emissão de NFS-e diretamente do Contas a Receber */}
      <EmitirNfseModal
        isOpen={isNfseModalOpen}
        onClose={() => {
          setIsNfseModalOpen(false);
          setContaParaNfse(null);
        }}
        onSuccess={() => {
          fetchData();
        }}
        initialConta={contaParaNfse}
      />
    </div>
  );
}
export default FinanceiroPage;
